import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  clearStoredSession,
  login,
  logout,
  me,
  readStoredToken,
  register,
  storeSession,
  TIERS,
  type Tier,
  type User,
} from '@/lib/api/auth';

interface AuthValue {
  readonly user: User | null;
  readonly token: string | null;
  readonly isAuthenticated: boolean;
  /** True until a stored session has been confirmed or discarded. */
  readonly loading: boolean;
  signUp: (firstName: string, lastName: string, email: string, password: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  hasTierAccess: (tier: Tier) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Session state for the SPA (port of the bundle's AuthProvider, index-legacy-main l.8036–8060).
 * A stored token is restored only after GET /api/auth/me.php confirms it; any other answer, or
 * a failed request, discards it. Password recovery is the static /recover/ page (ML-D4), so no
 * recovery method is offered here.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  // Read once, during the first render, so the expiry rule runs before any child reads the token.
  const [storedToken] = useState(readStoredToken);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(storedToken !== null);

  useEffect(() => {
    if (storedToken === null) return;
    let cancelled = false;
    me(storedToken)
      .then((restored) => {
        if (cancelled) return;
        if (restored === null) {
          clearStoredSession();
          return;
        }
        setUser(restored);
        setToken(storedToken);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.warn('Auth restore error:', error);
        clearStoredSession();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storedToken]);

  const signUp = useCallback(async (firstName: string, lastName: string, email: string, password: string) => {
    const session = await register({ firstName, lastName, email, password });
    storeSession(session.token);
    setToken(session.token);
    setUser(session.user);
    return session.user;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await login({ email, password });
    storeSession(session.token);
    setToken(session.token);
    setUser(session.user);
    return session.user;
  }, []);

  const signOut = useCallback(async () => {
    const current = token;
    setUser(null);
    setToken(null);
    clearStoredSession();
    if (current !== null) {
      try {
        await logout(current);
      } catch (error) {
        console.warn('Logout error:', error);
      }
    }
  }, [token]);

  const hasTierAccess = useCallback(
    (tier: Tier) => user !== null && TIERS.indexOf(user.tier) >= TIERS.indexOf(tier),
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      token,
      isAuthenticated: user !== null && token !== null,
      loading,
      signUp,
      signIn,
      signOut,
      hasTierAccess,
    }),
    [user, token, loading, signUp, signIn, signOut, hasTierAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook is the provider's public API (imported as '@/context/AuthContext')
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
